/**
 * useMessageInbox — Aggregation hook for the Zulip-style messaging inbox.
 *
 * Fetches recent ModComments across all entities, groups by Account (customer)
 * then by entity type within each account. Resolves entity names and account
 * ownership, computes unread counts.
 */

import { useMemo, useState, useEffect } from "react";
import { useList } from "@refinedev/core";
import axios from "axios";
import { useEntityNames, getEntityInfo } from "./useEntityName";
import { resolveCommentAuthor, stripContactPrefix } from "@/utils/commentAuthor";

export interface ThreadSummary {
  /** Entity WSID (the thread/topic ID) */
  entityId: string;
  /** Resolved entity name */
  entityName: string;
  /** Module label (e.g. "Policies", "Threats") */
  moduleLabel: string;
  /** Internal module name for routing */
  moduleName: string;
  /** Navigation icon name */
  moduleIcon: string;
  /** Route to the entity's show page */
  entityRoute: string;
  /** Account WSID this entity belongs to */
  accountId: string;
  /** Resolved account name */
  accountName: string;
  /** Total comment count in this thread */
  commentCount: number;
  /** Unread comment count (since last seen) */
  unreadCount: number;
  /** Last comment content (preview) */
  lastMessage: string;
  /** Last comment author */
  lastAuthor: string;
  /** Last comment timestamp (ISO) */
  lastTime: string;
}

/** Account group containing entity-type subgroups */
export interface AccountGroup {
  accountId: string;
  accountName: string;
  /** Subgroups by entity type */
  moduleGroups: ModuleSubgroup[];
  /** Total unread across all threads in this account */
  totalUnread: number;
  /** Most recent activity timestamp */
  lastTime: string;
}

export interface ModuleSubgroup {
  label: string;
  moduleName: string;
  icon: string;
  threads: ThreadSummary[];
  totalUnread: number;
}

/** Flat group by module (legacy, used by filter pills) */
export interface InboxGroup {
  label: string;
  moduleName: string;
  icon: string;
  threads: ThreadSummary[];
  totalUnread: number;
}

// coreBOS module → field that references the Account
const ACCOUNT_FIELDS: Record<string, string> = {
  evPolicies: "policytoaccount",
  Threats: "company",
  Audit: "organization_id",
  evDataFlow: "rel_account",
  ProcessingActivities: "rel_account",
};

// localStorage key prefix for last-seen timestamps
const LAST_SEEN_PREFIX = "msg_last_seen_";

function getLastSeen(entityId: string): string {
  return localStorage.getItem(`${LAST_SEEN_PREFIX}${entityId}`) || "";
}

export function markThreadSeen(entityId: string) {
  localStorage.setItem(
    `${LAST_SEEN_PREFIX}${entityId}`,
    new Date().toISOString()
  );
}

/** In-memory cache for entity → account mapping */
const accountCache = new Map<string, string>();
const accountNameCache = new Map<string, string>();
/** Cache email → account WSID */
const emailAccountCache = new Map<string, string>();

/**
 * Batch-resolve account ownership for a list of entity WSIDs.
 *
 * Resolution strategy (two paths):
 * 1. Direct: Entity → account reference field (policytoaccount, company, etc.)
 * 2. Via Contact email: comment's relatedassignedemail → Contact → account_id
 *
 * Path 2 is the fallback for entities without a direct account field
 * (ThreatsMeasures, evAcceptance, cbContent, Documents).
 */
async function resolveAccounts(
  entityIds: string[],
  entityInfoMap: Map<string, { coreBOSModule: string }>,
  /** Map of entityId → email from the comment's relatedassignedemail */
  entityEmails?: Map<string, string>
): Promise<{
  accountIds: Map<string, string>;
  accountNames: Map<string, string>;
}> {
  const accountIds = new Map<string, string>();

  // --- Path 1: Direct entity → account field ---
  const moduleGroups = new Map<string, string[]>();
  for (const id of entityIds) {
    if (accountCache.has(id)) {
      accountIds.set(id, accountCache.get(id)!);
      continue;
    }
    const info = entityInfoMap.get(id);
    if (!info) continue;
    const field = ACCOUNT_FIELDS[info.coreBOSModule];
    if (!field) continue;
    if (!moduleGroups.has(info.coreBOSModule)) {
      moduleGroups.set(info.coreBOSModule, []);
    }
    moduleGroups.get(info.coreBOSModule)!.push(id);
  }

  for (const [mod, ids] of moduleGroups) {
    const field = ACCOUNT_FIELDS[mod];
    const idList = ids.map((id) => `'${id}'`).join(", ");
    try {
      const res = await axios.post("/api/direct/query", {
        query: `SELECT id, ${field} FROM ${mod} WHERE id IN (${idList}) LIMIT ${ids.length};`,
      });
      if (res.data.success) {
        for (const row of res.data.result || []) {
          const accId = row[field];
          if (accId) {
            accountIds.set(row.id, accId);
            accountCache.set(row.id, accId);
          }
        }
      }
    } catch {
      /* ignore */
    }
  }

  // --- Path 2: Fallback via Contact email → account_id ---
  if (entityEmails) {
    // Collect emails for unresolved entities
    const unresolvedEmails = new Set<string>();
    for (const id of entityIds) {
      if (accountIds.has(id)) continue; // already resolved
      const email = entityEmails.get(id);
      if (email && !emailAccountCache.has(email)) {
        unresolvedEmails.add(email);
      }
    }

    // Batch-query Contacts by email to get their account_id
    if (unresolvedEmails.size > 0) {
      const emailList = [...unresolvedEmails]
        .map((e) => `'${e}'`)
        .join(", ");
      try {
        const res = await axios.post("/api/direct/query", {
          query: `SELECT id, email, account_id FROM Contacts WHERE email IN (${emailList}) LIMIT ${unresolvedEmails.size};`,
        });
        if (res.data.success) {
          for (const row of res.data.result || []) {
            if (row.email && row.account_id) {
              emailAccountCache.set(row.email, row.account_id);
            }
          }
        }
      } catch {
        /* ignore */
      }
    }

    // Apply email → account resolution for unresolved entities
    for (const id of entityIds) {
      if (accountIds.has(id)) continue;
      const email = entityEmails.get(id);
      if (email) {
        const accId = emailAccountCache.get(email);
        if (accId) {
          accountIds.set(id, accId);
          accountCache.set(id, accId);
        }
      }
    }
  }

  // --- Resolve account names ---
  const uniqueAccountIds = [...new Set(accountIds.values())].filter(
    (id) => !accountNameCache.has(id)
  );

  if (uniqueAccountIds.length > 0) {
    const idList = uniqueAccountIds.map((id) => `'${id}'`).join(", ");
    try {
      const res = await axios.post("/api/direct/query", {
        query: `SELECT id, accountname FROM Accounts WHERE id IN (${idList}) LIMIT ${uniqueAccountIds.length};`,
      });
      if (res.data.success) {
        for (const row of res.data.result || []) {
          accountNameCache.set(row.id, row.accountname);
        }
      }
    } catch {
      /* ignore */
    }
  }

  const accountNames = new Map<string, string>();
  for (const [entityId, accId] of accountIds) {
    accountNames.set(entityId, accountNameCache.get(accId) || accId);
  }

  return { accountIds, accountNames };
}

export function useMessageInbox(options?: {
  pageSize?: number;
  refetchInterval?: number;
}) {
  const pageSize = options?.pageSize || 200;
  const refetchInterval = options?.refetchInterval || 30000;

  // Fetch all recent comments (no related_to filter)
  const {
    data: commentsData,
    isLoading,
    refetch,
  } = useList({
    resource: "comments",
    pagination: { pageSize },
    sorters: [{ field: "createdtime", order: "desc" }],
    queryOptions: { refetchInterval },
  });

  const comments = (commentsData?.data || []) as any[];

  // Extract unique entity WSIDs + build info map + collect emails
  const { entityIds, entityInfoMap, entityEmails } = useMemo(() => {
    const ids = new Set<string>();
    const infoMap = new Map<string, { coreBOSModule: string }>();
    const emails = new Map<string, string>();
    for (const c of comments) {
      if (c.related_to) {
        ids.add(c.related_to);
        const info = getEntityInfo(c.related_to);
        if (info) {
          infoMap.set(c.related_to, { coreBOSModule: info.coreBOSModule });
        }
        // Collect the portal contact email from the comment
        if (c.relatedassignedemail && !emails.has(c.related_to)) {
          emails.set(c.related_to, c.relatedassignedemail);
        }
      }
    }
    return {
      entityIds: Array.from(ids),
      entityInfoMap: infoMap,
      entityEmails: emails,
    };
  }, [comments]);

  // Batch resolve entity names
  const { names: entityNames, loading: namesLoading } =
    useEntityNames(entityIds);

  // Resolve account ownership
  const [accountData, setAccountData] = useState<{
    accountIds: Map<string, string>;
    accountNames: Map<string, string>;
  }>({ accountIds: new Map(), accountNames: new Map() });
  const [accountsLoading, setAccountsLoading] = useState(false);

  useEffect(() => {
    if (entityIds.length === 0) return;
    setAccountsLoading(true);
    resolveAccounts(entityIds, entityInfoMap, entityEmails).then((data) => {
      setAccountData(data);
      setAccountsLoading(false);
    });
  }, [entityIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build thread summaries grouped by account → entity type
  const { threads, accountGroups, groups, totalUnread } = useMemo(() => {
    const threadMap = new Map<string, ThreadSummary>();

    for (const c of comments) {
      const entityId = c.related_to;
      if (!entityId) continue;

      const info = getEntityInfo(entityId);
      if (!info) continue;

      if (!threadMap.has(entityId)) {
        const accId = accountData.accountIds.get(entityId) || "";
        const accName = accountData.accountNames.get(entityId) || "";
        threadMap.set(entityId, {
          entityId,
          entityName: entityNames.get(entityId) || entityId,
          moduleLabel: info.label,
          moduleName: info.moduleName,
          moduleIcon: info.icon,
          entityRoute: info.route,
          accountId: accId,
          accountName: accName,
          commentCount: 0,
          unreadCount: 0,
          lastMessage: "",
          lastAuthor: "",
          lastTime: "",
        });
      }

      const thread = threadMap.get(entityId)!;
      thread.commentCount++;

      // Check if unread
      const lastSeen = getLastSeen(entityId);
      if (c.createdtime && c.createdtime > lastSeen) {
        thread.unreadCount++;
      }

      // First comment in the sorted list is the most recent
      if (!thread.lastTime) {
        thread.lastMessage = stripContactPrefix(c.commentcontent || "").slice(0, 160);
        thread.lastAuthor = resolveCommentAuthor(c).name;
        thread.lastTime = c.createdtime || "";
      }
    }

    // Sort threads by last time
    const allThreads = Array.from(threadMap.values()).sort(
      (a, b) => (b.lastTime || "").localeCompare(a.lastTime || "")
    );

    // ---- Account-level grouping (primary) ----
    const acctGroupMap = new Map<string, AccountGroup>();
    for (const thread of allThreads) {
      const key = thread.accountId || "_none";
      if (!acctGroupMap.has(key)) {
        acctGroupMap.set(key, {
          accountId: thread.accountId,
          accountName: thread.accountName || "Senza cliente",
          moduleGroups: [],
          totalUnread: 0,
          lastTime: "",
        });
      }
      const acctGroup = acctGroupMap.get(key)!;
      acctGroup.totalUnread += thread.unreadCount;
      if (!acctGroup.lastTime || thread.lastTime > acctGroup.lastTime) {
        acctGroup.lastTime = thread.lastTime;
      }

      // Find or create module subgroup
      let modSub = acctGroup.moduleGroups.find(
        (mg) => mg.moduleName === thread.moduleName
      );
      if (!modSub) {
        modSub = {
          label: thread.moduleLabel,
          moduleName: thread.moduleName,
          icon: thread.moduleIcon,
          threads: [],
          totalUnread: 0,
        };
        acctGroup.moduleGroups.push(modSub);
      }
      modSub.threads.push(thread);
      modSub.totalUnread += thread.unreadCount;
    }

    const sortedAccountGroups = Array.from(acctGroupMap.values()).sort(
      (a, b) =>
        b.totalUnread - a.totalUnread ||
        (b.lastTime || "").localeCompare(a.lastTime || "")
    );

    // ---- Flat module grouping (for filter pills) ----
    const groupMap = new Map<string, InboxGroup>();
    for (const thread of allThreads) {
      if (!groupMap.has(thread.moduleName)) {
        groupMap.set(thread.moduleName, {
          label: thread.moduleLabel,
          moduleName: thread.moduleName,
          icon: thread.moduleIcon,
          threads: [],
          totalUnread: 0,
        });
      }
      const group = groupMap.get(thread.moduleName)!;
      group.threads.push(thread);
      group.totalUnread += thread.unreadCount;
    }

    const sortedGroups = Array.from(groupMap.values()).sort(
      (a, b) =>
        b.totalUnread - a.totalUnread || a.label.localeCompare(b.label)
    );

    const totalUnread = allThreads.reduce(
      (sum, t) => sum + t.unreadCount,
      0
    );

    return {
      threads: allThreads,
      accountGroups: sortedAccountGroups,
      groups: sortedGroups,
      totalUnread,
    };
  }, [comments, entityNames, accountData]);

  return {
    threads,
    accountGroups,
    groups,
    totalUnread,
    isLoading: isLoading || namesLoading || accountsLoading,
    refetch,
  };
}
